import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Download, FileText, Table, Loader2, FileSpreadsheet } from "lucide-react";

export default function ExportacaoRelatorios({ dados, nomeArquivo = "relatorio", colunas }) {
  const [exporting, setExporting] = useState(false);

  const formatCurrency = (value) => {
    if (typeof value !== 'number') return value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (value, withTime = false) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return withTime ? d.toLocaleString('pt-BR') : d.toLocaleDateString('pt-BR');
  };

  const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

  const exportarCSV = () => {
    if (!dados || dados.length === 0) return;
    
    const headers = colunas.map(c => escapeCsv(c.label)).join(';');
    const rows = dados.map(item => 
      colunas.map(c => {
        const value = c.accessor(item);
        if (c.type === 'currency') return escapeCsv(String(formatCurrency(value)).replace('R$', '').trim());
        if (c.type === 'date' && value) return escapeCsv(`="${formatDate(value)}"`);
        return escapeCsv(value || '');
      }).join(';')
    ).join('\n');

    const csv = `${headers}\n${rows}`;
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${nomeArquivo}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportarPDF = async () => {
    setExporting(true);
    
    // Criar HTML para impressão
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${nomeArquivo}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1B4332; border-bottom: 2px solid #D4A373; padding-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #1B4332; color: white; padding: 10px; text-align: left; }
          td { padding: 8px; border-bottom: 1px solid #ddd; }
          tr:nth-child(even) { background: #f9f9f9; }
          .currency { text-align: right; }
          .header { display: flex; justify-content: space-between; align-items: center; }
          .date { color: #666; font-size: 12px; }
          .total-row { background: #e5e5e5 !important; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${nomeArquivo.replace(/_/g, ' ')}</h1>
          <span class="date">Gerado em: ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR')}</span>
        </div>
        <table>
          <thead>
            <tr>
              ${colunas.map(c => `<th>${c.label}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${dados.map(item => `
              <tr>
                ${colunas.map(c => {
                  const value = c.accessor(item);
                  let displayValue = value || '-';
                  if (c.type === 'currency') displayValue = formatCurrency(value);
                  if (c.type === 'date' && value) displayValue = new Date(value).toLocaleDateString('pt-BR');
                  return `<td class="${c.type === 'currency' ? 'currency' : ''}">${displayValue}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
        <p style="margin-top: 20px; color: #666; font-size: 12px;">
          Total de registros: ${dados.length}
        </p>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      setExporting(false);
    }, 500);
  };

  const exportarExcel = () => {
    if (!dados || dados.length === 0) return;
    const headers = colunas.map(c => c.label);
    const rows = dados.map(item => colunas.map(c => {
      const value = c.accessor(item);
      if (c.type === 'currency') return formatCurrency(value);
      if (c.type === 'date' && value) return formatDate(value);
      return value ?? '';
    }));

    const tableHtml = `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
        </head>
        <body>
          <table>
            <thead>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${rows.map((r) => `<tr>${r.map((v, colIndex) => {
                const col = colunas[colIndex];
                const safeValue = String(v).replace(/</g,'&lt;').replace(/>/g,'&gt;');
                const style = col?.type === 'date' ? ' style="mso-number-format:\'dd\\/mm\\/yyyy\';"' : '';
                return `<td${style}>${safeValue}</td>`;
              }).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>`;

    const blob = new Blob(['\ufeff' + tableHtml], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${nomeArquivo}_${new Date().toISOString().split('T')[0]}.xls`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2" disabled={!dados || dados.length === 0}>
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Exportar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={exportarExcel} className="gap-2">
          <FileSpreadsheet className="w-4 h-4" />
          Exportar Excel
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportarPDF} className="gap-2">
          <FileText className="w-4 h-4" />
          Exportar PDF
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportarCSV} className="gap-2">
          <Table className="w-4 h-4" />
          Exportar CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
